import type { ID, IgAccount } from "@ml/shared";
import { useToast } from "@ml/ui";
import { useQuery } from "@tanstack/react-query";
import { useLocation, useNavigate } from 'react-router';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { igAccountsApi, isApiError } from "./api";
import { useAuth } from "./auth";
import { accountSwitchDestination, followDecision } from "./workspace";
import { NoIgAccount } from "../components/NoIgAccount";
import { PageSkeleton } from "../components/FullPage";

export const IG_ACCOUNTS_KEY = ["ig-accounts"] as const;
/** Lives under IG_ACCOUNTS_KEY, so invalidating the account list refreshes the overview too. */
export const IG_ACCOUNTS_OVERVIEW_KEY = ["ig-accounts", "overview"] as const;

export interface IgAccountContextValue {
  accounts: IgAccount[];
  /** The account every feature page works on. null until one is connected. */
  current: IgAccount | null;
  setCurrent: (id: string) => void;
  /** A person's explicit selection also closes the previous account's editors and detail URL. */
  switchAccount: (id: string) => void;
  isLoading: boolean;
  error: unknown;
  refetch: () => void;
}

const IgAccountContext = createContext<IgAccountContextValue | null>(null);

function storageKey(userId: string) {
  return `gf.igAccount.${userId}`;
}

function readStored(userId: string | undefined): string | null {
  if (!userId) return null;
  try {
    return localStorage.getItem(storageKey(userId));
  } catch {
    return null;
  }
}

/** Mounted once by the app layout. Lists GET /api/ig-accounts and remembers the selection per user. */
export function IgAccountProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const navigate = useNavigate(), location = useLocation();
  const q = useQuery({
    queryKey: IG_ACCOUNTS_KEY,
    queryFn: async () => (await igAccountsApi.list()).items,
    enabled: !!user,
    // Engine status (running / paused) is pushed by the extension's heartbeat; keep it fresh.
    refetchInterval: 60_000,
  });
  const [selectedId, setSelectedId] = useState<string | null>(() => readStored(user?.id));

  useEffect(() => {
    setSelectedId(readStored(user?.id));
  }, [user?.id]);

  const accounts = useMemo(() => q.data ?? [], [q.data]);
  const current = useMemo(() => accounts.find((a) => a.id === selectedId) ?? accounts[0] ?? null, [accounts, selectedId]);

  const setCurrent = useCallback(
    (id: string) => {
      if (!accounts.some(account => account.id === id)) return;
      setSelectedId(id);
      if (!user) return;
      try {
        localStorage.setItem(storageKey(user.id), id);
      } catch {
        /* storage blocked — selection lasts for this session */
      }
    },
    [user, accounts],
  );

  const switchAccount = useCallback((id: string) => {
    if (id === current?.id || !accounts.some(account => account.id === id)) return;
    navigate(accountSwitchDestination(location.pathname, location.search), { replace: true });
    setCurrent(id);
  }, [accounts, current?.id, location.pathname, location.search, navigate, setCurrent]);

  const refetchAccounts = q.refetch;
  const value = useMemo<IgAccountContextValue>(
    () => ({
      accounts,
      current,
      setCurrent,
      switchAccount,
      isLoading: q.isPending && !!user,
      error: q.error,
      refetch: () => void refetchAccounts(),
    }),
    [accounts, current, setCurrent, switchAccount, q.isPending, q.error, user, refetchAccounts],
  );

  return <IgAccountContext.Provider value={value}>{children}</IgAccountContext.Provider>;
}

/**
 * `const { accounts, current, setCurrent } = useIgAccount();`
 * Scope every feature query by `current.id` and include it in the query key.
 */
export function useIgAccount(): IgAccountContextValue {
  const ctx = useContext(IgAccountContext);
  if (!ctx) throw new Error("useIgAccount() must be used inside <IgAccountProvider> (the app layout mounts it).");
  return ctx;
}

const CurrentAccountContext = createContext<IgAccount | null>(null);

/**
 * Wrap a feature page that needs a LinkedIn account. Shows a skeleton while
 * loading and the "connect your account" empty state when there's none; inside,
 * `useCurrentIgAccount()` is guaranteed non-null.
 */
export function RequireIgAccount({ children }: { children: ReactNode }) {
  const { current, isLoading } = useIgAccount();
  if (isLoading) return <PageSkeleton />;
  if (!current) return <NoIgAccount />;
  // A new workspace must never inherit an editor's unsaved text or selected recipients.
  return <CurrentAccountContext.Provider key={current.id} value={current}>{children}</CurrentAccountContext.Provider>;
}

/** The selected IG account. Only call below <RequireIgAccount>. */
export function useCurrentIgAccount(): IgAccount {
  const acc = useContext(CurrentAccountContext);
  if (!acc) throw new Error("useCurrentIgAccount() must be used inside <RequireIgAccount>.");
  return acc;
}

/**
 * GET /api/ig-accounts/overview — every connected account with today's activity, contact count and
 * automation switch, plus the plan limit. A 404 (older API) isn't retried; callers fall back to
 * `useIgAccount().accounts` without the per-account numbers.
 */
export function useIgAccountsOverview({ enabled = true }: { enabled?: boolean } = {}) {
  const { user } = useAuth();
  return useQuery({
    queryKey: IG_ACCOUNTS_OVERVIEW_KEY,
    queryFn: igAccountsApi.overview,
    enabled: !!user && enabled,
    refetchInterval: 60_000,
    retry: (count, err) => !isApiError(err, 404) && count < 2,
  });
}

/**
 * Deep links: call on a detail page/drawer with the loaded entity's igAccountId (undefined while
 * it loads). If the entity belongs to another connected account, the workspace switches to it so
 * the header always matches the content. If the user later switches accounts themselves, `onLeave`
 * runs (close the drawer / go back to the list) instead of switching them back.
 *
 * Returns true while a switch is pending, so a page can hold off rendering account-scoped UI.
 */
export function useFollowEntityAccount(igAccountId: ID | null | undefined, onLeave?: () => void): boolean {
  const { accounts, current, setCurrent } = useIgAccount();
  const toast = useToast();
  const synced = useRef<ID | null>(null);
  const announced = useRef<ID | null>(null);
  const leaveRef = useRef(onLeave);
  leaveRef.current = onLeave;

  const currentId = current?.id ?? null;
  const accountIds = useMemo(() => accounts.map((a) => a.id), [accounts]);
  const idsKey = accountIds.join(",");
  const decision = followDecision({ entityAccountId: igAccountId, currentId, accountIds, syncedFor: synced.current });

  useEffect(() => {
    if (igAccountId && igAccountId === currentId) {
      synced.current = igAccountId;
      return;
    }
    const d = followDecision({ entityAccountId: igAccountId, currentId, accountIds, syncedFor: synced.current });
    if (d === "follow" && igAccountId) {
      setCurrent(igAccountId);
      const acc = accounts.find((a) => a.id === igAccountId);
      if (acc && announced.current !== igAccountId) {
        announced.current = igAccountId;
        toast.info(`Switched to @${acc.username}`, "What you opened belongs to that LinkedIn account.");
      }
    } else if (d === "leave") {
      leaveRef.current?.();
    }
    // accountIds/accounts are covered by idsKey; toast/setCurrent are stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [igAccountId, currentId, idsKey]);

  return decision === "follow";
}
